import React, { createContext, useContext, useState, useCallback } from 'react';
import CustomAlert from '../../components/ui/CustomAlert';
import CustomConfirmationAlert from '../../components/ui/CustomConfirmationAlert';

const CustomAlertContext = createContext(null);

export const CustomAlertProvider = ({ children }) => {
  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancelar',
    onConfirm: () => {},
    onCancel: null,
    type: 'warning'
  });

  const [multiActionState, setMultiActionState] = useState({
    isOpen: false,
    title: '',
    message: '',
    primaryButtonText: 'Salvar e Sair',
    secondaryButtonText: 'Cancelar',
    tertiaryButtonText: 'Sair sem Salvar',
    onPrimary: () => {},
    onSecondary: () => {},
    onTertiary: () => {},
    type: 'warning'
  });

  const hideAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const hideMultiActionAlert = useCallback(() => {
    setMultiActionState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const showAlert = useCallback(({ 
    title, 
    message, 
    confirmText = 'OK', 
    cancelText = 'Cancelar',
    onConfirm = () => hideAlert(),
    onCancel = null,
    type = 'warning'
  }) => {
    setAlertState({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText,
      onConfirm: () => {
        onConfirm();
        hideAlert();
      },
      onCancel: onCancel ? () => {
        onCancel();
        hideAlert();
      } : null,
      type
    });
  }, [hideAlert]);

  const showConfirmation = useCallback(({ 
    title, 
    message, 
    confirmText = 'OK', 
    cancelText = 'Cancelar',
    onConfirm,
    onCancel = () => {},
    type = 'warning'
  }) => {
    showAlert({
      title,
      message,
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
      type
    });
  }, [showAlert]);

  const showExitConfirmation = useCallback(({
    title = 'Confirmação',
    message = 'Deseja sair sem salvar os dados?',
    primaryButtonText = 'Salvar e Sair',
    secondaryButtonText = 'Cancelar',
    tertiaryButtonText = 'Sair sem Salvar',
    onSaveAndExit,
    onExitWithoutSaving,
    onCancel = () => {},
    type = 'warning'
  }) => {
    setMultiActionState({
      isOpen: true,
      title,
      message,
      primaryButtonText,
      secondaryButtonText,
      tertiaryButtonText,
      onPrimary: () => {
        onSaveAndExit();
        hideMultiActionAlert();
      },
      onSecondary: () => {
        onCancel();
        hideMultiActionAlert();
      },
      onTertiary: () => {
        onExitWithoutSaving();
        hideMultiActionAlert();
      },
      type
    });
  }, [hideMultiActionAlert]);

  return (
    <CustomAlertContext.Provider value={{ 
      showAlert, 
      showConfirmation, 
      showExitConfirmation,
      hideAlert 
    }}>
      {children}
      <CustomAlert 
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        onConfirm={alertState.onConfirm}
        onCancel={alertState.onCancel}
        type={alertState.type}
      />
      <CustomConfirmationAlert 
        isOpen={multiActionState.isOpen}
        title={multiActionState.title}
        message={multiActionState.message}
        primaryButtonText={multiActionState.primaryButtonText}
        secondaryButtonText={multiActionState.secondaryButtonText}
        tertiaryButtonText={multiActionState.tertiaryButtonText}
        onPrimary={multiActionState.onPrimary}
        onSecondary={multiActionState.onSecondary}
        onTertiary={multiActionState.onTertiary}
        type={multiActionState.type}
      />
    </CustomAlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(CustomAlertContext);
  if (!context) {
    throw new Error('useAlert must be used within a CustomAlertProvider');
  }
  return context;
};

export default CustomAlertProvider;